import { useState } from 'react';
import type { KeyMoment } from '../api/mediatorService';
import type { Party } from '../types/case';
import { humanizeSpeakerRefs } from '../lib/speakers';

interface Props {
  moments: KeyMoment[];
  parties: Party[];
  onAdd: (summary: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  adding: boolean;
  /** Default collapsed state. `false` (collapsed) by default to save vertical space. */
  defaultOpen?: boolean;
}

const sourceIcon = (source: KeyMoment['source']): string =>
  source === 'ai' ? '🤖' : '👤';

export function KeyMomentsPanel({
  moments,
  parties,
  onAdd,
  onDelete,
  adding,
  defaultOpen = false,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState('');

  const submit = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    await onAdd(trimmed);
    setDraft('');
    setComposing(false);
    setOpen(true); // show the new moment after saving
  };

  return (
    <section className="card">
      <button
        type="button"
        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <span aria-hidden className="text-sm">
            {open ? '▾' : '▸'}
          </span>
          <span className="section-label">Key moments ({moments.length})</span>
        </div>
        <span
          className="btn-secondary !py-0.5 !text-xs"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            setComposing((c) => !c);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              setComposing((c) => !c);
              setOpen(true);
            }
          }}
        >
          {composing ? 'Cancel' : '+ Add'}
        </span>
      </button>

      {open && (
        <div className="space-y-2 px-3 pb-3">
          {composing && (
            <div className="space-y-1.5">
              <textarea
                className="w-full resize-none rounded-md border border-surface-border p-2 text-sm focus:border-brand focus:outline-none"
                rows={2}
                placeholder="What did you notice?"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={adding}
                autoFocus
              />
              <div className="flex justify-end gap-1">
                <button
                  type="button"
                  className="btn-primary !py-1 !text-xs"
                  onClick={() => void submit()}
                  disabled={adding || draft.trim().length < 3}
                >
                  {adding ? 'Saving…' : 'Save moment'}
                </button>
              </div>
            </div>
          )}

          {moments.length === 0 ? (
            !composing && (
              <p className="text-xs text-gray-500">
                Nothing starred yet. The copilot auto-pins important statements; you can also add
                your own with <span className="font-medium">+ Add</span>.
              </p>
            )
          ) : (
            <ol className="space-y-2 text-sm">
              {moments.map((m) => (
                <li key={m.id} className="flex items-start gap-2">
                  <span className="mt-0.5" aria-hidden>
                    ⭐
                  </span>
                  <div className="flex-1">
                    <p className="text-gray-800">
                      {humanizeSpeakerRefs(m.summary, parties)}
                    </p>
                    <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500">
                      <span aria-hidden>{sourceIcon(m.source)}</span>
                      <span>{m.source === 'ai' ? 'AI' : 'You'}</span>
                      <span>·</span>
                      <span>{m.moment_type.replace('_', ' ')}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded px-1 text-xs text-gray-400 hover:text-red-600"
                    onClick={() => void onDelete(m.id)}
                    title="Remove"
                    aria-label="Remove moment"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}
